import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { supabase } from '@/lib/supabase';

export interface TrustedBrand {
  name: string;
  logo: string | null;
  /** URL réelle du site du marchand (domaine personnalisé ou sous-domaine) */
  url: string | null;
}

export interface PlatformStatsData {
  /** Total des visites générées par tous les sites de la plateforme */
  visits: number;
  /** Nombre de produits ajoutés sur toute la plateforme */
  products: number;
  /** Nombre de services proposés sur toute la plateforme */
  services: number;
  /** Nombre de boutiques / sites créés */
  sites: number;
  /** Sites Zifek (nom + logo) pour le bandeau « ils nous font confiance » */
  brands: TrustedBrand[];
}

interface PlatformStatsContextType {
  stats: PlatformStatsData;
  loading: boolean;
  error: string | null;
  retry: () => void;
}

const EMPTY_STATS: PlatformStatsData = {
  visits: 0,
  products: 0,
  services: 0,
  sites: 0,
  brands: [],
};

const VISIT_TABLES = [
  'visitesiteweb',
  'visiteboutique',
  'visiteproduit',
  'visiteproduitsiteweb',
  'visiteservicesiteweb',
  'visitelandingpage',
];

const PlatformStatsContext = createContext<PlatformStatsContextType | null>(null);

async function countTable(table: string): Promise<number> {
  const { count, error } = await supabase
    .from(table)
    .select('id', { count: 'exact', head: true });
  if (error) return 0;
  return count || 0;
}

/**
 * Compte les services réellement proposés sur la plateforme.
 * La table historique `services` est restée vide : les services vivent
 * désormais dans `nospartenairesservices`, `booking_services` (réservations)
 * et dans les annonces marketplace de type « services ». On additionne les
 * trois pour refléter la réalité (évite un « 0 services » trompeur).
 */
async function countServices(): Promise<number> {
  const [siteServices, bookingServices] = await Promise.all([
    countTable('nospartenairesservices'),
    countTable('booking_services'),
  ]);
  let marketplaceServices = 0;
  try {
    const { count } = await supabase
      .from('annonce')
      .select('id', { count: 'exact', head: true })
      .eq('type', 'services');
    marketplaceServices = count || 0;
  } catch {
    marketplaceServices = 0;
  }
  return siteServices + bookingServices + marketplaceServices;
}

/**
 * Compte les SITES réellement créés sur Zifek : uniquement les comptes
 * professionnels inscrits via /register (les comptes client et les comptes
 * internes comme le superadmin ou les développeurs ne sont pas des sites).
 */
async function countProfessionalSites(): Promise<number> {
  const { count, error } = await supabase
    .from('users')
    .select('id', { count: 'exact', head: true })
    .eq('active', 1)
    .not('typecompte', 'in', '(0,1,6,8)');
  if (error) return 0;
  return count || 0;
}

export function PlatformStatsProvider({ children }: { children: ReactNode }) {
  const [stats, setStats] = useState<PlatformStatsData>(EMPTY_STATS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [visitCounts, products, services, sites, brandRows] = await Promise.all([
        Promise.all(VISIT_TABLES.map(countTable)),
        countTable('product_items'),
        countServices(),
        countProfessionalSites(),
        supabase
          .from('userwebsite')
          .select('user_name, image, iduser, statut')
          .order('date', { ascending: false })
          .limit(60),
      ]);

      const visits = visitCounts.reduce((sum, n) => sum + n, 0);

      const rawBrands = (brandRows.data || []) as {
        user_name?: string | null;
        image?: string | null;
        iduser?: number | null;
      }[];

      // Récupère les domaines personnalisés des marchands pour rendre
      // les logos cliquables vers leur vrai site, ainsi que le LOGO réel du
      // commerce (il vit dans `users.image`, pas dans `userwebsite`).
      const userIds = rawBrands.map((r) => r.iduser).filter((v): v is number => typeof v === 'number');
      const domainMap = new Map<number, string>();
      const brandLogoMap = new Map<number, string>();
      const brandNameMap = new Map<number, string>();
      if (userIds.length > 0) {
        const [domainsRes, usersRes] = await Promise.all([
          supabase
            .from('websitedomain')
            .select('user_id, domaine')
            .in('user_id', userIds),
          supabase
            .from('users')
            .select('id, image, name, nomcommerce')
            .in('id', userIds),
        ]);
        (domainsRes.data || []).forEach((d: { user_id?: number | null; domaine?: string | null }) => {
          if (typeof d.user_id === 'number' && d.domaine) {
            domainMap.set(d.user_id, d.domaine);
          }
        });
        (usersRes.data || []).forEach((u: { id: number; image?: string | null; name?: string | null; nomcommerce?: string | null }) => {
          if (u.image && u.image.trim()) brandLogoMap.set(u.id, u.image.trim());
          const label = (u.nomcommerce || u.name || '').trim();
          if (label) brandNameMap.set(u.id, label);
        });
      }

      const brands: TrustedBrand[] = rawBrands
        .map((row) => ({
          name: (row.iduser != null ? brandNameMap.get(row.iduser) : '') || row.user_name || '',
          logo: (row.image && row.image.trim()) || (row.iduser != null ? brandLogoMap.get(row.iduser) : '') || null,
          url: row.iduser != null && domainMap.has(row.iduser) ? `https://${domainMap.get(row.iduser)}` : null,
        }))
        .filter((b) => b.name || b.logo);

      setStats({ visits, products, services, sites, brands });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement des statistiques');
      setStats(EMPTY_STATS);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return (
    <PlatformStatsContext.Provider value={{ stats, loading, error, retry: fetchStats }}>
      {children}
    </PlatformStatsContext.Provider>
  );
}

export function usePlatformStats(): PlatformStatsContextType {
  const context = useContext(PlatformStatsContext);
  if (!context) {
    throw new Error('usePlatformStats must be used within a PlatformStatsProvider');
  }
  return context;
}